
    import Vue from "vue";
    import Component from "vue-class-component";
    import { Ref } from "vue-property-decorator";
    import ApiClient from "@/utilities/ApiClient";
    import Utils from "@/utilities/Utils";
    import { VForm } from "@/vForm";
    import * as toastr from "toastr";
    import ApiButton from "@/vue/components/ApiButton.vue";
    import ConfirmDialogue from "@/vue/components/ConfirmDialogue.vue";
    import DatePicker from "@/vue/components/DatePicker.vue";
    import FileUpload from "@/vue/components/FileUpload.vue";
    import ColourPicker from "@/vue/components/ColourPicker.vue";
    import HtmlEditor from "@/vue/components/HtmlEditor.vue";
    import { ISaveResponse } from "@/model/ISaveResponse";
    import { IClient, Client } from "@/model/Client";
    import { ILookupItem } from "@/model/LookupItem";
    import { BannerOption, WorkPermitFormType } from "@/model/Enums";

    @Component({ components: { ApiButton, ConfirmDialogue, DatePicker, FileUpload, ColourPicker, HtmlEditor } })
    export default class ClientDialogue extends Vue {

        //
        // -- properties
        //

        @Ref("clientForm") readonly clientForm!: VForm;
        showDialogue: boolean = false;
        publicApiKey: string = "";
        client: Client = new Client();

        get canDelete(): boolean {
            return !Utils.isEmptyId(this.client.id);
        }

        get bannerOptions(): Array<ILookupItem> {
            return Utils.enumToLookups(BannerOption);
        }

        tabName: string = "tab-details";

        get dialogueTitle(): string {
            return Utils.isEmptyId(this.client.id) ? "Add Client" : `Edit Client - ${this.client.name}`;
        }

        get qrCodeUrl(): string {
            return ApiClient.resolveUrl(`api/client/qrcode?clientID=${this.client.id}`);
        }

        get qrCodeFileName(): string {
            return `qr-code-${Utils.dashify(this.client.name)}.png`
        }

        get workPermitFormTypesOptions(): Array<ILookupItem> {
            return Utils.enumToLookups(WorkPermitFormType).filter(i => i.id !== 0);
        }

        //
        // -- methods
        //

        async showPreview(): Promise<void> {
            const clientUrl = "https://" + this.client.host;
            const isOk = await this.checkUrl(clientUrl);
            const url = isOk
                ? clientUrl
                : `${ApiClient.baseUrl}?clientid=${this.client.id}`
            console.log(`checkUrl isOk = ${isOk} - clientUrl=${clientUrl} - url=${url}`);
            const win = window.open(url, "_blank");
            win?.focus();
        }

        async checkUrl(url:string): Promise<boolean> {
            try{
                const response = await fetch(url, { method: "HEAD", mode: "no-cors" });
                console.log("Fetch result = ", response);
                return true;
            }
            catch (e: any) {
                console.log("Fetch error = ", e);
                return false;
            }
        }

        validateColour(value: string|null|undefined): boolean|string {
            if(!value) return "Please specify a colour";
            const regex = /^#([a-fA-F0-9]{6}|[a-fA-F0-9]{3})$/;
            if(!regex.test(value)) return "Incorrect format!";
            return true;
        }

        isChecked(item: ILookupItem): boolean {
            const formType: WorkPermitFormType = Number(item.id);
            return this.client.workPermitFormTypes.indexOf(formType) > -1;
        }

        onCheckedChanged(item: ILookupItem, isChecked: boolean) {
            const formType: WorkPermitFormType = Number(item.id);
            const index = this.client.workPermitFormTypes.indexOf(formType);
            if(index > -1 && !isChecked) {
                this.client.workPermitFormTypes.splice(index, 1);
            }
            if(index == -1 && isChecked) {
                this.client.workPermitFormTypes.push(formType);
            }
        }

        confirmNewApiKey(): void {
            const dialog: ConfirmDialogue = this.$refs.apiKeyConfirmDialogue as ConfirmDialogue;
            dialog.show();
        }

        async createNewApiKey(): Promise<void> {
            const response = await ApiClient.get(`/api/client/newApiKey?clientID=${this.client.id}`, "create-new-api-key") as { apiKey: string };
            this.publicApiKey = response.apiKey;
        }

        async copyApiKey(): Promise<void> {
            if (!this.publicApiKey) return;
            await navigator.clipboard.writeText(this.publicApiKey);
            toastr.info("API Key Copied");
        }

        //
        //
        //

        async show(id: string): Promise<void> {
            this.reset();
            // still go to server for new client to get default values
            const clientData: IClient = await ApiClient.get(`api/client/Load?id=${id}`);
            this.client.update(clientData);
            this.showDialogue = true;
        }

        close() {
            this.reset();
            this.publicApiKey = "";
            this.tabName = "tab-details";
            this.showDialogue = false;
        }

        reset(): void {
            Utils.resetObject(this.client);
            this.clientForm?.resetValidation();
            const fileUpload: FileUpload = this.$refs.fileUpload as FileUpload;
            fileUpload?.clear();
        }

        async save(): Promise<void> {
            if (!this.clientForm.validate()) {
                toastr.warning("Please fix highlighted issues", "Cannot Save");
                return;
            }
            const response: ISaveResponse = await ApiClient.post("/api/client/save", this.client, "client-save");
            if(this.client.isNew) this.client.id = response.newID;
            toastr.success("Saved");
            this.$emit("saved");
            this.showDialogue = false;
        }

        confirmDelete(): void {
            const dialog: ConfirmDialogue = this.$refs.deleteDialogue as ConfirmDialogue;
            dialog.show();
        }

        async doDelete(): Promise<void> {
            await ApiClient.post("/api/client/delete", this.client, "client-delete");
            toastr.warning("Deleted");
            this.$emit("saved");
            this.close();
        }
    }
